import { useMemo } from 'react';
import { Box, SimpleGrid, Icon, useColorModeValue, Text, Flex, Button } from '@chakra-ui/react';
import { MdPeople, MdAssignment, MdLocalHospital, MdPersonSearch } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import Card from 'components/card/Card';
import TodayJornada from 'components/layouts/dashboard/TodayJornada';
import { useAppointments } from 'hooks/useAppointments';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { EstadoCita } from 'types/models';

function todayRange() {
  const desde = new Date(); desde.setHours(0, 0, 0, 0);
  const hasta  = new Date(); hasta.setHours(23, 59, 59, 999);
  return { desde: desde.toISOString(), hasta: hasta.toISOString() };
}

export default function MedicoDashboard() {
  const brandColor = useColorModeValue('brand.500', 'white');
  const boxBg      = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
  const textColor  = useColorModeValue('secondaryGray.900', 'white');
  const textMuted  = useColorModeValue('gray.500', 'gray.400');
  const navigate   = useNavigate();
  const { user }   = useUser();
  const { data: me } = useCurrentDbUser();

  const nombre = user?.firstName ?? 'Médico';
  const range  = useMemo(todayRange, []);

  const { data: todayData } = useAppointments(
    me?.id ? { desde: range.desde, hasta: range.hasta, professionalId: me.id, limit: 50 } : {}
  );

  const consultasHoy    = todayData?.data?.length ?? 0;
  const consultasPend   = todayData?.data?.filter(a => a.estado === EstadoCita.CONFIRMADA).length ?? 0;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify='space-between' align='center' mb='24px' flexWrap='wrap' gap='12px'>
        <Box>
          <Text color={textColor} fontSize='2xl' fontWeight='700'>
            Bienvenido, Dr. {nombre}
          </Text>
          <Text color={textMuted} fontSize='sm'>Panel Médico</Text>
        </Box>
        <Button
          leftIcon={<Icon as={MdPersonSearch} />}
          colorScheme='brand'
          onClick={() => navigate('/admin/patients')}>
          Ver Pacientes
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap='20px' mb='20px'>
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdPeople} color={brandColor} />} />
          }
          name='Pacientes Atendidos'
          value='—'
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdAssignment} color={brandColor} />} />
          }
          name='Consultas Hoy'
          value={consultasHoy > 0 ? String(consultasHoy) : '—'}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg={boxBg}
              icon={<Icon w='32px' h='32px' as={MdLocalHospital} color={brandColor} />} />
          }
          name='Pendientes Hoy'
          value={consultasHoy > 0 ? String(consultasPend) : '—'}
        />
        <MiniStatistics
          startContent={
            <IconBox w='56px' h='56px' bg='linear-gradient(90deg, #4481EB 0%, #04BEFE 100%)'
              icon={<Icon w='28px' h='28px' as={MdPeople} color='white' />} />
          }
          name='Total Pacientes'
          value='—'
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap='20px' mb='20px'>
        <Card p='20px'>
          <Text color={textColor} fontSize='lg' fontWeight='700' mb='16px'>
            Acciones Rápidas
          </Text>
          <Flex direction='column' gap='10px'>
            <Button variant='outline' colorScheme='brand' justifyContent='flex-start'
              leftIcon={<Icon as={MdPersonSearch} />}
              onClick={() => navigate('/admin/patients')}>
              Buscar Paciente
            </Button>
            <Button variant='outline' colorScheme='brand' justifyContent='flex-start'
              leftIcon={<Icon as={MdPeople} />}
              onClick={() => navigate('/admin/patients')}>
              Nuevo Paciente
            </Button>
          </Flex>
        </Card>

        <Card p='20px'>
          <Text color={textColor} fontSize='lg' fontWeight='700' mb='8px'>
            Agenda
          </Text>
          <Text color={textMuted} fontSize='sm' mb='12px'>
            Gestiona tus consultas y derivaciones desde el módulo de agenda.
          </Text>
          <Button variant='outline' colorScheme='teal' size='sm' onClick={() => navigate('/admin/agenda')}>
            Ir a Agenda
          </Button>
        </Card>
      </SimpleGrid>

      <TodayJornada title='Mi Jornada de Hoy' />
    </Box>
  );
}
